/**
 * Dialog 'Tipos de contrato': lista (nome + Badge da modalidade + Switch ativo
 * via PUT) e form inline nome + modalidade → POST.
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { ContractModalitySchema, MODALITY_LABELS } from '@erp/shared';
import type { ContractModality } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { contractTypesOptions, errorMessage } from './billing-queries';
import { useCreateContractType, useUpdateContractType } from './billing-mutations';

const MODALITIES = ContractModalitySchema.options;

export function ContractTypesDialog({ onClose }: { onClose: () => void }) {
  const typesQ = useQuery(contractTypesOptions());
  const create = useCreateContractType();
  const update = useUpdateContractType();

  const [name, setName] = useState('');
  const [modality, setModality] = useState<'' | ContractModality>('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || modality === '') return;
    create.mutate(
      { name: name.trim(), modality },
      {
        onSuccess: () => {
          setName('');
          setModality('');
        },
      }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Tipos de contrato" className="sm:max-w-lg">
      {typesQ.isPending ? (
        <div className="space-y-2" aria-busy>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : typesQ.isError ? (
        <p className="text-sm text-destructive">
          Erro ao carregar tipos: {errorMessage(typesQ.error)}
        </p>
      ) : typesQ.data.length === 0 ? (
        <EmptyState className="h-28" message="Nenhum tipo de contrato cadastrado" />
      ) : (
        <div className="rounded-md border">
          {typesQ.data.map((t) => (
            <div
              key={t.id}
              className="flex h-11 items-center gap-2.5 border-b border-divider px-3 last:border-0"
            >
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{t.name}</span>
              <Badge variant="primary">{MODALITY_LABELS[t.modality]}</Badge>
              <Switch
                checked={t.active}
                disabled={update.isPending}
                onCheckedChange={(checked) =>
                  update.mutate({
                    id: t.id,
                    body: { name: t.name, modality: t.modality, active: checked },
                  })
                }
                aria-label={t.active ? `Inativar ${t.name}` : `Ativar ${t.name}`}
              />
            </div>
          ))}
        </div>
      )}
      {update.error ? (
        <p className="mt-2 text-sm text-destructive">
          Erro ao atualizar tipo: {errorMessage(update.error)}
        </p>
      ) : null}

      {/* Form inline: novo tipo */}
      <form onSubmit={submit} className="mt-4 border-t border-divider pt-4">
        <Label className="text-[13px]">Novo tipo</Label>
        <div className="mt-1.5 flex items-center gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome do tipo…"
            aria-label="Nome do tipo de contrato"
            className="h-8 flex-1 text-sm"
          />
          <Select
            value={modality}
            onChange={(e) => setModality(e.target.value as ContractModality | '')}
            aria-label="Modalidade"
            className="h-8 w-44"
          >
            <option value="" disabled>
              Modalidade…
            </option>
            {MODALITIES.map((m) => (
              <option key={m} value={m}>
                {MODALITY_LABELS[m]}
              </option>
            ))}
          </Select>
          <Button
            type="submit"
            size="sm"
            disabled={create.isPending || !name.trim() || modality === ''}
          >
            <Plus />
            Adicionar
          </Button>
        </div>
        {create.error ? (
          <p className="mt-2 text-sm text-destructive">
            Erro ao criar tipo: {errorMessage(create.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
